import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  Devices as DevicesIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const DeviceUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [userDevices, setUserDevices] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('all');

  // Form data
  const [formData, setFormData] = useState({
    full_name: '',
    department: '',
    position: '',
    is_active: true,
    notes: ''
  });

  useEffect(() => {
    fetchUsers();
  }, [search, departmentFilter, isActiveFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 1000
      };
      if (search) params.search = search;
      if (departmentFilter) params.department = departmentFilter;
      if (isActiveFilter !== 'all') params.is_active = isActiveFilter === 'active';

      const response = await axios.get('/device-users/', { params });
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError('Ошибка загрузки пользователей устройств');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (userToEdit = null) => {
    if (userToEdit) {
      setEditMode(true);
      setSelectedUser(userToEdit);
      setFormData({
        full_name: userToEdit.full_name || '',
        department: userToEdit.department || '',
        position: userToEdit.position || '',
        is_active: userToEdit.is_active ?? true,
        notes: userToEdit.notes || ''
      });
    } else {
      setEditMode(false);
      setSelectedUser(null);
      setFormData({
        full_name: '',
        department: '',
        position: '',
        is_active: true,
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedUser(null);
  };

  const handleSave = async () => {
    try {
      if (editMode) {
        await axios.put(`/device-users/${selectedUser.id}`, formData);
      } else {
        await axios.post('/device-users/', formData);
      }
      handleCloseDialog();
      fetchUsers();
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
      console.error(err);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Удалить этого пользователя? Это действие необратимо.')) return;

    try {
      await axios.delete(`/device-users/${userId}`);
      fetchUsers();
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления');
      console.error(err);
    }
  };

  const handleViewDevices = async (deviceUser) => {
    try {
      const response = await axios.get(`/device-users/${deviceUser.id}/devices`);
      setUserDevices(response.data.accesses || []);
      setSelectedUser(deviceUser);
      setDetailDialogOpen(true);
    } catch (err) {
      setError('Ошибка загрузки устройств пользователя');
      console.error(err);
    }
  };

  const getAccessTypeLabel = (type) => {
    const labels = {
      local: 'Локальный',
      remote: 'Удаленный',
      console: 'Консольный',
      physical: 'Физический',
      administrative: 'Административный'
    };
    return labels[type] || type;
  };

  const getAccessLevelLabel = (level) => {
    const labels = {
      read_only: 'Просмотр',
      operator: 'Оператор',
      admin: 'Администратор',
      full_access: 'Полный доступ'
    };
    return labels[level] || level;
  };

  const getAccessLevelColor = (level) => {
    const colors = {
      read_only: 'default',
      operator: 'info',
      admin: 'warning',
      full_access: 'error'
    };
    return colors[level] || 'default';
  };

  const canManage = user?.is_superuser || user?.permissions?.includes('device_users.manage');
  const canView = user?.is_superuser || user?.permissions?.includes('device_users.view');

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">У вас нет прав для просмотра пользователей устройств</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Пользователи устройств</Typography>
          <Tooltip title="Справочник людей, которые работают с оборудованием. Здесь можно управлять информацией о пользователях и назначать им доступ к устройствам с определенными правами." arrow placement="top">
            <InfoIcon sx={{ fontSize: 20, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Добавить пользователя
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Поиск"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Имя, отдел, должность..."
              sx={{ flexGrow: 1, minWidth: 300 }}
            />
            <TextField
              label="Отдел"
              variant="outlined"
              size="small"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Статус"
              variant="outlined"
              size="small"
              select
              value={isActiveFilter}
              onChange={(e) => setIsActiveFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">Все</MenuItem>
              <MenuItem value="active">Активные</MenuItem>
              <MenuItem value="inactive">Неактивные</MenuItem>
            </TextField>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Имя</TableCell>
              <TableCell>Отдел</TableCell>
              <TableCell>Должность</TableCell>
              <TableCell align="center">Устройств</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    {loading ? 'Загрузка...' : 'Пользователи не найдены'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((deviceUser) => (
                <TableRow key={deviceUser.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="500">
                      {deviceUser.full_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{deviceUser.department || '—'}</TableCell>
                  <TableCell>{deviceUser.position || '—'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={deviceUser.device_count || 0}
                      size="small"
                      color={deviceUser.device_count > 0 ? 'primary' : 'default'}
                      icon={<DevicesIcon />}
                      onClick={() => handleViewDevices(deviceUser)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={deviceUser.is_active ? 'Активен' : 'Неактивен'}
                      size="small"
                      color={deviceUser.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {canManage && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(deviceUser)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(deviceUser.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Редактировать пользователя' : 'Новый пользователь'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Имя"
              fullWidth
              required
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            />
            <TextField
              label="Отдел"
              fullWidth
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            />
            <TextField
              label="Должность"
              fullWidth
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            />
            <TextField
              label="Заметки"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Активен"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.full_name.trim()}
          >
            {editMode ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Devices Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Устройства пользователя: {selectedUser?.full_name}
        </DialogTitle>
        <DialogContent>
          {userDevices.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              У этого пользователя нет доступа к устройствам
            </Alert>
          ) : (
            <List sx={{ mt: 1 }}>
              {userDevices.map((access) => (
                <React.Fragment key={access.id}>
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="500">
                        Устройство ID: {access.device_id}
                      </Typography>
                      <Chip
                        label={access.is_active ? 'Активен' : 'Неактивен'}
                        size="small"
                        color={access.is_active ? 'success' : 'default'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip
                        label={`Тип: ${getAccessTypeLabel(access.access_type)}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`Уровень: ${getAccessLevelLabel(access.access_level)}`}
                        size="small"
                        color={getAccessLevelColor(access.access_level)}
                      />
                    </Box>
                    {access.description && (
                      <Typography variant="body2" color="text.secondary">
                        {access.description}
                      </Typography>
                    )}
                    {(access.valid_from || access.valid_until) && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        {access.valid_from && `С: ${new Date(access.valid_from).toLocaleDateString()}`}
                        {access.valid_from && access.valid_until && ' | '}
                        {access.valid_until && `До: ${new Date(access.valid_until).toLocaleDateString()}`}
                      </Typography>
                    )}
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceUsers;
